import { useCallback, useRef } from "react";
import {
  buildCoachFollowupInstructions,
  buildEvaluationToolResult,
  buildEvaluateProgressInstructions,
  buildFirstTrainingExerciseInstructions,
  buildGenerateScenarioInstructions,
  buildScenarioToolOutput,
  buildTrainingEvaluationRequest,
  buildTrainingObjective,
  buildTrainingRoleplayContext,
  createInitialTrainingExerciseState,
} from "@teleprompt/shared/realtime";

export default function useRealtimeTrainingFlow({
  isTrainingMode,
  meeting,
  trainingPlan,
  readyRef,
  sendEvent,
  sendResponseCreate,
}) {
  const evaluationLockTimeoutMs = 12000;
  const trainingObjective = buildTrainingObjective(trainingPlan);
  const trainingExerciseStateRef = useRef(
    createInitialTrainingExerciseState(trainingObjective)
  );
  const lastTrainingEvaluationRequestRef = useRef(null);
  const trainingEvaluationInFlightRef = useRef(false);
  const lastEvaluationTranscriptRef = useRef("");
  const evaluationLockTimeoutRef = useRef(null);

  const clearEvaluationInFlight = useCallback(() => {
    trainingEvaluationInFlightRef.current = false;
    if (evaluationLockTimeoutRef.current) {
      clearTimeout(evaluationLockTimeoutRef.current);
      evaluationLockTimeoutRef.current = null;
    }
  }, []);

  const isEvaluationInFlight = useCallback(
    () => trainingEvaluationInFlightRef.current,
    []
  );

  const resetTrainingFlow = useCallback(() => {
    trainingExerciseStateRef.current = createInitialTrainingExerciseState(trainingObjective);
    trainingEvaluationInFlightRef.current = false;
    lastTrainingEvaluationRequestRef.current = null;
    lastEvaluationTranscriptRef.current = "";
    if (evaluationLockTimeoutRef.current) {
      clearTimeout(evaluationLockTimeoutRef.current);
      evaluationLockTimeoutRef.current = null;
    }
  }, [trainingObjective]);

  const setCurrentTrainingExercise = useCallback((exercise) => {
    const currentExercise = String(exercise || "").trim().slice(0, 1200);
    if (!currentExercise) return;
    trainingExerciseStateRef.current = {
      ...trainingExerciseStateRef.current,
      currentExercise,
      hasStarted: true,
    };
  }, []);

  const continueAfterTrainingToolCall = useCallback(
    (callId, name, args = {}) => {
      if (!isTrainingMode || !callId) return;
      if (name !== "evaluate_progress_and_advance" && name !== "generate_next_scenario") return;

      console.log("[TrainingTool] received", { callId, name, args });

      let output;
      let shouldRequestNextScenario = false;
      let usedRefinementThisTurn = false;
      let decisionReason = "";
      const roleplayContext = buildTrainingRoleplayContext(meeting);

      if (name === "evaluate_progress_and_advance") {
        const currentExerciseState = trainingExerciseStateRef.current;
        clearEvaluationInFlight();
        const result = buildEvaluationToolResult(args, currentExerciseState);
        trainingExerciseStateRef.current = result.nextExerciseState;
        output = result.output;
        shouldRequestNextScenario = result.shouldRequestNextScenario;
        usedRefinementThisTurn = result.usedRefinementThisTurn;
        decisionReason = result.decisionReason;

        console.log("[TrainingTool] evaluation state transition", {
          shouldAdvance: output.shouldAdvance,
          reason: String(output.reason || "").slice(0, 220),
          stateBefore: currentExerciseState,
          stateAfter: trainingExerciseStateRef.current,
          transition: result.transition,
        });
      } else {
        clearEvaluationInFlight();
        output = buildScenarioToolOutput(args);
        setCurrentTrainingExercise(
          [output.scenario, output.exampleLine].filter(Boolean).join(" Practice line: ")
        );
      }

      sendEvent({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: callId,
          output: JSON.stringify(output),
        },
      });

      console.log("[TrainingTool] output sent", { callId, name, output });

      if (name === "evaluate_progress_and_advance" && shouldRequestNextScenario) {
        sendResponseCreate(
          {
            output_modalities: ["text"],
            tool_choice: {
              type: "function",
              name: "generate_next_scenario",
            },
            instructions: buildGenerateScenarioInstructions(
              decisionReason,
              "new_exercise",
              roleplayContext,
              trainingObjective
            ),
          },
          "training-next-scenario"
        );

        console.log("[TrainingTool] next scenario requested", {
          callId,
          name,
          comparisonContext: lastTrainingEvaluationRequestRef.current,
          evaluationDecision: output,
        });
        return;
      }

      sendResponseCreate(
        {
          output_modalities: ["audio"],
          tool_choice: "auto",
          instructions: [
            buildCoachFollowupInstructions(
              meeting?.languageLabel || "English",
              name === "generate_next_scenario",
              roleplayContext,
              trainingObjective,
              trainingExerciseStateRef.current.currentExercise
            ),
            usedRefinementThisTurn
              ? "Make this refinement concise and practical."
              : "Keep momentum high and move forward naturally.",
          ].join(" "),
        },
        "training-follow-up"
      );

      console.log("[TrainingTool] follow-up response requested", { callId, name });
    },
    [
      clearEvaluationInFlight,
      isTrainingMode,
      meeting,
      sendEvent,
      sendResponseCreate,
      setCurrentTrainingExercise,
      trainingObjective,
    ]
  );

  const requestTrainingProgressEvaluation = useCallback(
    (transcript) => {
      if (!isTrainingMode || !readyRef.current) return false;
      const trimmedTranscript = String(transcript || "").trim();
      if (!trimmedTranscript) return false;
      const currentExerciseState = trainingExerciseStateRef.current;
      if (!currentExerciseState.hasStarted) {
        sendResponseCreate(
          {
            output_modalities: ["audio"],
            tool_choice: "none",
            instructions: buildFirstTrainingExerciseInstructions(
              meeting?.languageLabel || "English",
              trainingObjective,
              buildTrainingRoleplayContext(meeting)
            ),
          },
          "training-follow-up"
        );
        return true;
      }
      if (trainingEvaluationInFlightRef.current) {
        console.log("[TrainingTool] evaluation skipped", {
          reason: "in_flight",
          transcript: trimmedTranscript.slice(0, 140),
          exerciseState: trainingExerciseStateRef.current,
        });
        return false;
      }
      if (lastEvaluationTranscriptRef.current === trimmedTranscript) {
        console.log("[TrainingTool] evaluation skipped", {
          reason: "duplicate_transcript",
          transcript: trimmedTranscript.slice(0, 140),
          exerciseState: trainingExerciseStateRef.current,
        });
        return false;
      }

      const previousAttempt = String(currentExerciseState.previousAttempt || "").trim();
      if (previousAttempt && previousAttempt === trimmedTranscript) {
        console.log("[TrainingTool] evaluation skipped", {
          reason: "same_as_previous_attempt",
          transcript: trimmedTranscript.slice(0, 140),
          exerciseState: currentExerciseState,
        });
        return false;
      }

      const evaluationRequest = buildTrainingEvaluationRequest(trimmedTranscript, currentExerciseState);
      lastTrainingEvaluationRequestRef.current = evaluationRequest;
      trainingEvaluationInFlightRef.current = true;
      lastEvaluationTranscriptRef.current = trimmedTranscript;
      if (evaluationLockTimeoutRef.current) {
        clearTimeout(evaluationLockTimeoutRef.current);
      }
      evaluationLockTimeoutRef.current = setTimeout(() => {
        trainingEvaluationInFlightRef.current = false;
        evaluationLockTimeoutRef.current = null;
      }, evaluationLockTimeoutMs);

      const sent = sendResponseCreate(
        {
          output_modalities: ["text"],
          tool_choice: {
            type: "function",
            name: "evaluate_progress_and_advance",
          },
          instructions: buildEvaluateProgressInstructions({
            trainingObjective,
            currentExercise: currentExerciseState.currentExercise,
            previousAttempt,
            currentAttempt: trimmedTranscript,
            retryCount: currentExerciseState.retryCount,
            exerciseId: currentExerciseState.exerciseId,
          }),
        },
        "training-progress-evaluation"
      );

      console.log("[TrainingTool] evaluate_progress_and_advance requested", {
        sent,
        ...evaluationRequest,
      });
      trainingExerciseStateRef.current = {
        ...currentExerciseState,
        previousAttempt: trimmedTranscript,
      };

      return sent;
    },
    [isTrainingMode, meeting, readyRef, sendResponseCreate, trainingObjective]
  );

  return {
    clearEvaluationInFlight,
    isEvaluationInFlight,
    continueAfterTrainingToolCall,
    requestTrainingProgressEvaluation,
    resetTrainingFlow,
    setCurrentTrainingExercise,
  };
}
